import React, { useEffect } from 'react';
import { X, ExternalLink, Sparkles, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { PROJECT_DEMOS } from './demos/ProjectDemos';

const ProjectDemoModal = ({ project, isOpen, onClose }) => {
  const { t } = useTranslation();

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !project) return null;

  const DemoComponent = PROJECT_DEMOS[project.id];

  const scrollToCalculator = () => {
    onClose();
    setTimeout(() => {
      const el = document.querySelector('[data-testid="calculator-title"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 bg-black/70 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
      data-testid="project-demo-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl my-4 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="close-demo-modal"
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-slate-800/80 hover:bg-slate-700 text-gray-300 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Hero banner */}
        <div className="relative h-40 md:h-52 overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/20"></div>
          <div className="absolute bottom-4 left-6 right-6">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/20 border border-blue-500/40 rounded-full mb-2">
              <Sparkles className="w-3 h-3 text-blue-300" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-blue-300">
                {t('projects.livePreview')}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white">{project.title}</h2>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-300 leading-relaxed">{project.description}</p>

          <div className="flex flex-wrap gap-1.5">
            {project.techStack.map((tech) => (
              <Badge key={tech} variant="secondary" className="text-[10px]">
                {tech}
              </Badge>
            ))}
          </div>

          {/* Demo container */}
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-slate-700/50 p-4">
            <div className="flex items-center gap-1.5 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/70"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70"></div>
              <span className="text-[10px] text-gray-500 ml-2 font-mono">
                {t('projects.tryItYourself')}
              </span>
            </div>
            {DemoComponent ? (
              <DemoComponent />
            ) : (
              <div className="py-10 text-center text-sm text-gray-500">
                {t('projects.demoComingSoon')}
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 italic text-center">
            {t('projects.demoDisclaimer')}
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={scrollToCalculator}
              data-testid="demo-cta-configure"
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-5 shadow-lg shadow-blue-500/30 group"
            >
              {t('projects.wantThisApp')}
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            {project.demoLink && (
              <Button
                onClick={() => window.open(project.demoLink, '_blank', 'noopener,noreferrer')}
                variant="outline"
                className="sm:w-auto border-slate-600 text-gray-200 hover:text-white hover:bg-slate-800 py-5"
              >
                {t('projects.openLiveDemo')}
                <ExternalLink className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDemoModal;
